import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  EmailAuthProvider,
  deleteUser,
  reauthenticateWithCredential,
  signOut,
  updatePassword,
} from 'firebase/auth';
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  where,
  writeBatch,
} from 'firebase/firestore';

import { auth, db } from '../config/firebase';
import './Profile.css';

const maskPhone = (v) => {
  const digits = String(v || '').replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 2) return digits;
  if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
};

async function batchDeleteByQuery(colName, whereField, whereValue) {
  // Deleta em lotes (até 450 por batch pra ficar seguro)
  const q = query(collection(db, colName), where(whereField, '==', whereValue));
  const snaps = await getDocs(q);
  if (snaps.empty) return 0;

  let deleted = 0;
  let batch = writeBatch(db);
  let count = 0;

  for (const d of snaps.docs) {
    batch.delete(d.ref);
    deleted += 1;
    count += 1;
    if (count >= 450) {
      await batch.commit();
      batch = writeBatch(db);
      count = 0;
    }
  }
  if (count > 0) await batch.commit();
  return deleted;
}

export default function Profile() {
  const navigate = useNavigate();
  const user = auth.currentUser;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Dados da empresa
  const [companyName, setCompanyName] = useState('');
  const [companyPhone, setCompanyPhone] = useState('');
  const [companyWhatsapp, setCompanyWhatsapp] = useState('');
  const [companyCity, setCompanyCity] = useState('');

  // Trocar senha
  const [newPassword, setNewPassword] = useState('');
  const [newPassword2, setNewPassword2] = useState('');

  // Excluir conta
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);

  const canDelete = useMemo(() => {
    return deleteConfirm.trim().toUpperCase() === 'EXCLUIR' && deletePassword.trim().length >= 6;
  }, [deleteConfirm, deletePassword]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    let mounted = true;

    (async () => {
      try {
        setLoading(true);
        setError('');
        const ref = doc(db, 'companies', user.uid);
        const snap = await getDoc(ref);
        if (!mounted) return;

        const data = snap.exists() ? snap.data() : {};
        setCompanyName(data.companyName || '');
        setCompanyPhone(maskPhone(data.phone || ''));
        setCompanyWhatsapp(maskPhone(data.whatsapp || ''));
        setCompanyCity(data.city || '');
      } catch (e) {
        if (mounted) setError('Não foi possível carregar seus dados.');
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [navigate, user]);

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const payload = {
        companyName: companyName.trim(),
        phone: String(companyPhone || '').replace(/\D/g, ''),
        whatsapp: String(companyWhatsapp || '').replace(/\D/g, ''),
        city: companyCity.trim(),
        email: user.email || '',
        updatedAt: new Date(),
      };

      await setDoc(doc(db, 'companies', user.uid), payload, { merge: true });
      setSuccess('Dados atualizados com sucesso!');
    } catch (e) {
      setError('Erro ao salvar. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    setError('');
    setSuccess('');

    if (!newPassword || newPassword.length < 6) {
      setError('A nova senha deve ter pelo menos 6 caracteres.');
      return;
    }
    if (newPassword !== newPassword2) {
      setError('As senhas não conferem.');
      return;
    }

    try {
      await updatePassword(auth.currentUser, newPassword);
      setNewPassword('');
      setNewPassword2('');
      setSuccess('Senha atualizada!');
    } catch (e) {
      // Normalmente precisa de login recente
      setError('Para trocar a senha, faça login novamente e tente de novo.');
    }
  };

  const handleDeleteAccount = async () => {
    const u = auth.currentUser;
    if (!u || !u.email) return;

    setDeleting(true);
    setError('');
    setSuccess('');

    try {
      // 1) Reautenticar
      const cred = EmailAuthProvider.credential(u.email, deletePassword);
      await reauthenticateWithCredential(u, cred);

      // 2) Apagar dados do Firestore (rotas, solicitações, empresa)
      // Ajuste as coleções conforme você estiver usando no projeto
      await batchDeleteByQuery('routes', 'userId', u.uid);
      await batchDeleteByQuery('adRequests', 'userId', u.uid).catch(() => 0);
      await batchDeleteByQuery('bannerRequests', 'userId', u.uid).catch(() => 0);

      // empresa (doc principal)
      await deleteDoc(doc(db, 'companies', u.uid)).catch(() => null);

      // 3) Apagar usuário do Auth
      await deleteUser(u);

      // 4) Garantir logout e redirecionar
      await signOut(auth).catch(() => null);
      navigate('/');
    } catch (e) {
      const msg = String(e?.code || e?.message || '').toLowerCase();
      if (msg.includes('auth/wrong-password')) setError('Senha incorreta.');
      else if (msg.includes('auth/too-many-requests')) setError('Muitas tentativas. Aguarde e tente novamente.');
      else if (msg.includes('auth/requires-recent-login')) setError('Faça login novamente e tente excluir a conta.');
      else setError('Não foi possível excluir a conta.');
    } finally {
      setDeleting(false);
    }
  };

  if (!user) return null;

  return (
    <div className="pf-page">
      <header className="pf-header">
        <div className="pf-header-inner">
          <div className="pf-brand" onClick={() => navigate('/')} role="button" tabIndex={0}>
            <span className="pf-dot" />
            <span className="pf-title">Minha Empresa</span>
          </div>

          <div className="pf-actions">
            <button className="pf-btn ghost" onClick={() => navigate('/cadastro')}>Cadastrar Rotas</button>
            <button className="pf-btn danger" onClick={() => setDeleteOpen(true)}>Excluir Conta</button>
          </div>
        </div>
      </header>

      <main className="pf-main">
        <div className="pf-grid">
          <section className="pf-card">
            <h2 className="pf-card-title">Dados da empresa</h2>

            {loading ? (
              <div className="pf-muted">Carregando...</div>
            ) : (
              <>
                {error && <div className="pf-alert error">{error}</div>}
                {success && <div className="pf-alert ok">{success}</div>}

                <div className="pf-form">
                  <label className="pf-label">Nome</label>
                  <input className="pf-input" value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="Nome da empresa" />

                  <div className="pf-row">
                    <div>
                      <label className="pf-label">Telefone</label>
                      <input
                        className="pf-input"
                        value={companyPhone}
                        onChange={(e) => setCompanyPhone(maskPhone(e.target.value))}
                        placeholder="(00) 00000-0000"
                      />
                    </div>
                    <div>
                      <label className="pf-label">WhatsApp</label>
                      <input
                        className="pf-input"
                        value={companyWhatsapp}
                        onChange={(e) => setCompanyWhatsapp(maskPhone(e.target.value))}
                        placeholder="(00) 00000-0000"
                      />
                    </div>
                  </div>

                  <label className="pf-label">Cidade (opcional)</label>
                  <input className="pf-input" value={companyCity} onChange={(e) => setCompanyCity(e.target.value)} placeholder="Ex: João Pessoa, PB" />

                  <label className="pf-label">E-mail</label>
                  <input className="pf-input" value={user.email || ''} disabled />

                  <button className="pf-btn primary" onClick={handleSave} disabled={saving}>
                    {saving ? 'Salvando...' : 'Salvar alterações'}
                  </button>
                </div>
              </>
            )}
          </section>

          <section className="pf-card">
            <h2 className="pf-card-title">Segurança</h2>
            <div className="pf-form">
              <label className="pf-label">Nova senha</label>
              <input className="pf-input" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />

              <label className="pf-label">Confirmar nova senha</label>
              <input className="pf-input" type="password" value={newPassword2} onChange={(e) => setNewPassword2(e.target.value)} />

              <button className="pf-btn" onClick={handleChangePassword}>Atualizar senha</button>

              <div className="pf-muted" style={{ marginTop: 10 }}>
                Dica: o Firebase pode exigir “login recente” para trocar a senha. Se der erro, faça login novamente.
              </div>
            </div>
          </section>
        </div>
      </main>

      {deleteOpen && (
        <div className="pf-modal-overlay" onClick={() => !deleting && setDeleteOpen(false)}>
          <div className="pf-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pf-modal-head">
              <div>
                <div className="pf-modal-title">Excluir conta</div>
                <div className="pf-muted">Isso vai remover sua empresa, suas rotas e solicitações de anúncio.</div>
              </div>
              <button className="pf-icon-btn" onClick={() => !deleting && setDeleteOpen(false)} aria-label="Fechar">×</button>
            </div>

            <div className="pf-modal-body">
              <div className="pf-alert warn">
                Atenção: essa ação é <b>permanente</b>.
              </div>

              <label className="pf-label">Digite sua senha para confirmar</label>
              <input
                className="pf-input"
                type="password"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                placeholder="Sua senha"
              />

              <label className="pf-label">Digite EXCLUIR para confirmar</label>
              <input
                className="pf-input"
                value={deleteConfirm}
                onChange={(e) => setDeleteConfirm(e.target.value)}
                placeholder="EXCLUIR"
              />

              {error && <div className="pf-alert error" style={{ marginTop: 10 }}>{error}</div>}
            </div>

            <div className="pf-modal-foot">
              <button className="pf-btn ghost" onClick={() => !deleting && setDeleteOpen(false)} disabled={deleting}>Cancelar</button>
              <button className="pf-btn danger" onClick={handleDeleteAccount} disabled={!canDelete || deleting}>
                {deleting ? 'Excluindo...' : 'Excluir definitivamente'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
